import React, { useRef, useState } from 'react';
import { Camera, Loader2, Check } from 'lucide-react';
import { uploadProfilePic } from '../services/api';
import { toast } from 'sonner';

// Compress any image down to <=5KB JPEG (100x100, iterative quality).
async function compressToTinyJpeg(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });

  const renderToBlob = (size, quality) => new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const minDim = Math.min(img.width, img.height);
    const sx = (img.width - minDim) / 2;
    const sy = (img.height - minDim) / 2;
    ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

  const MAX = 5 * 1024;
  // Try 100px at decreasing quality, then shrink to 80px and 64px.
  for (const size of [100, 80, 64]) {
    for (let q = 0.85; q >= 0.25; q -= 0.1) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await renderToBlob(size, q);
      if (blob && blob.size <= MAX) return blob;
    }
  }
  // Final fallback — return whatever 64px @ 0.2 produces.
  return renderToBlob(64, 0.2);
}

export const UploadProfilePic = ({ userId, onUploaded, size = 88 }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = userId ? `${apiBase}/api/files/profile/${userId}?v=${bust}` : null;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error('Please choose an image');
      return;
    }
    setBusy(true);
    try {
      const blob = await compressToTinyJpeg(f);
      if (blob.size > 5 * 1024) {
        toast.error('Could not shrink image under 5 KB. Try a simpler photo.');
        return;
      }
      await uploadProfilePic(blob);
      setBust(Date.now());
      toast.success('Profile picture updated');
      onUploaded && onUploaded();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="upload-profile-pic">
      <div
        className="relative rounded-full overflow-hidden border-2 border-amber-200 bg-amber-50 flex items-center justify-center text-amber-700 font-semibold"
        style={{ width: size, height: size }}
      >
        {imgUrl ? (
          // onError keeps initials visible if user has no avatar yet.
          <img
            src={imgUrl}
            alt="avatar"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
          data-testid="profile-pic-upload-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {busy ? 'Compressing…' : 'Change photo'}
        </button>
        <p className="text-[11px] text-gray-500 leading-tight">Auto-shrunk to under 5&nbsp;KB.<br/>JPG / PNG / WEBP.</p>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="profile-pic-file-input" />
      </div>
    </div>
  );
};

export default UploadProfilePic;
